import { useState, useCallback, useEffect, useRef } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import { StatisticsDataType, StatisticsFilters } from '../../../types/statistics'
import {
    getStatisticsData,
    STATISTICS_REQUEST_TIMEOUT_MS,
} from '../../../services/statisticsService'

export const useComparisonData = (
    dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null,
    filters: StatisticsFilters,
    enabled: boolean = true,
    refreshKey: number = 0,
    analysisFeature?: string,
    comparisonWindowDays?: number
) => {
    const [comparisonData, setComparisonData] = useState<{
        previous: StatisticsDataType | null
    }>({ previous: null })
    const requestIdRef = useRef(0)
    const isMountedRef = useRef(false)
    const abortControllerRef = useRef<AbortController | null>(null)

    useEffect(() => {
        isMountedRef.current = true

        return () => {
            abortControllerRef.current?.abort()
            abortControllerRef.current = null
            isMountedRef.current = false
            requestIdRef.current += 1
        }
    }, [])

    const calculateComparisonData = useCallback(async () => {
        if (!enabled || !dateRange) {
            abortControllerRef.current?.abort()
            abortControllerRef.current = null
            return
        }

        abortControllerRef.current?.abort()
        setComparisonData({ previous: null })
        const abortController = new AbortController()
        abortControllerRef.current = abortController
        const requestId = ++requestIdRef.current
        const currentEnd = dateRange[1]
        const currentStart = dateRange[0]
        const selectedDays = currentEnd.diff(currentStart, 'day') + 1
        const daysDiff = comparisonWindowDays ?? selectedDays

        const previousEnd =
            comparisonWindowDays === undefined
                ? currentStart.subtract(1, 'day')
                : currentEnd.subtract(daysDiff, 'day')
        const previousStart = previousEnd.subtract(daysDiff - 1, 'day')
        const isCurrentRequest = () =>
            isMountedRef.current && requestId === requestIdRef.current

        try {
            const response = await getStatisticsData(
                previousStart.format('YYYY-MM-DD'),
                previousEnd.format('YYYY-MM-DD'),
                filters,
                analysisFeature,
                {
                    signal: abortController.signal,
                    timeout: STATISTICS_REQUEST_TIMEOUT_MS,
                }
            )

            if (!isCurrentRequest()) {
                return
            }

            setComparisonData({
                previous: {
                    ...response.summary,
                    speciesSummary: response.speciesSummary,
                },
            })
        } catch (error) {
            if (!isCurrentRequest()) {
                return
            }

            if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
                return
            }

            console.error('Failed to fetch comparison statistics:', error)
            if (isAxiosError(error) && error.response?.status === 401) {
                return
            }

            setComparisonData({ previous: null })
        } finally {
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null
            }
        }
    }, [
        analysisFeature,
        comparisonWindowDays,
        dateRange,
        enabled,
        filters,
    ])

    useEffect(() => {
        void calculateComparisonData()
    }, [calculateComparisonData, refreshKey])

    return { comparisonData }
}
